import React, { ReactElement } from 'react';
import { Box, BulletListContent, BulletListIcon, Li, Typography } from '@vp/swan';
import { Validation, ValidationState } from '@vp/upload-and-print-validator';
import { usePerfectBound } from 'src/hooks/usePerfectBound';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import failedValidationBullet from 'src/images/validation/failedValidationBullet.svg';
import successfulValidationBullet from 'src/images/validation/successfulValidationBullet.svg';
import unvalidatedBullet from 'src/images/validation/unvalidatedBullet.svg';
import warningValidationBullet from 'src/images/validation/warningValidationBullet.svg';
import { ValidationNames } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { getUnEmbeddedFilePages } from 'src/utils/validationUtil';

export type FileRequirementBulletProps = {
  requirement: Validation;
  isMobile: boolean;
  isValidating: boolean;
};

const BulletSvgForValidationState = (validationState: ValidationState) => {
  switch (validationState) {
    case ValidationState.Ok:
      return successfulValidationBullet;
    case ValidationState.Warning:
      return warningValidationBullet;
    case ValidationState.Error:
      return failedValidationBullet;
    case ValidationState.UnValidated:
      return unvalidatedBullet;
    default:
      return unvalidatedBullet;
  }
};

export const FileRequirementBullet = ({
  requirement,
  isMobile,
  isValidating,
}: FileRequirementBulletProps): ReactElement => {
  const { isPerfectBound, getPerfectBoundPageCountTokenValues } = usePerfectBound();
  const tokenValues = isPerfectBound ? getPerfectBoundPageCountTokenValues(requirement) : requirement.tokenValues;

  const { validations } = useValidationContext();
  const corruptValidator = validations?.find(validation => validation.id === ValidationNames.CorruptValidator);
  const unEmbeddedPages = corruptValidator?.details?.pagesData;

  const bulletText = text(requirement.title, tokenValues);

  const validationErrorMessage =
    unEmbeddedPages?.length > 0
      ? text(requirement.message, { pageNumbers: getUnEmbeddedFilePages(unEmbeddedPages) })
      : text('validations.corrupt file.error-description');
  const messageText =
    requirement.id === ValidationNames.CorruptValidator
      ? validationErrorMessage
      : requirement.message && text(requirement.message, tokenValues);

  const bullet = BulletSvgForValidationState(requirement.state);

  return (
    <>
      <Li pb={'1'}>
        <BulletListIcon src={bullet} width="32" height="32" alt={text('image-alt-message.bullet-icon')} />
        <BulletListContent>
          <Typography fontSize={isMobile ? 'xsmall' : 'small'}>{bulletText}</Typography>
        </BulletListContent>
      </Li>
      {!isValidating && (requirement.state === 'Error' || requirement.state === 'Warning') && messageText && (
        <Box marginLeft={'7'} style={{ marginTop: -10, paddingTop: 5 }}>
          <Typography
            fontSize={isMobile ? 'xsmall' : 'small'}
            textColor={requirement.state === 'Error' ? 'error' : 'subtle'}
          >
            {messageText}
          </Typography>
        </Box>
      )}
    </>
  );
};
